<template>
  <div class="flex flex-wrap items-center gap-x-3 gap-y-2 py-1" :data-testid="testId">
    <div class="w-[150px]">
      <v-select
        :model-value="modelValue?.field ?? null"
        :items="FIELD_OPTIONS"
        label="Campo"
        variant="outlined"
        density="compact"
        color="primary20"
        hide-details
        class="bg-white"
        :data-testid="`${testId}-field`"
        @update:model-value="onField"
      />
    </div>
    <div v-if="modelValue?.field === QualificationRuleField.SEAL" class="w-[240px]">
      <v-select
        :model-value="modelValue.key || null"
        :items="sealOptions"
        label="Selo"
        variant="outlined"
        density="compact"
        color="primary20"
        hide-details
        class="bg-white"
        :data-testid="`${testId}-seal`"
        @update:model-value="onKey"
      />
    </div>
    <div v-else-if="modelValue?.field === QualificationRuleField.DOCUMENT" class="w-[260px]">
      <v-autocomplete
        :model-value="modelValue.key || null"
        :items="documentOptions"
        label="Documento"
        variant="outlined"
        density="compact"
        color="primary20"
        hide-details
        class="bg-white"
        no-data-text="Nenhum tipo no catálogo"
        :data-testid="`${testId}-document`"
        @update:model-value="onKey"
      />
    </div>
    <OnFailSelector
      :model-value="modelValue?.onFail ?? QualificationOnFail.BLOCK"
      :test-id="`${testId}-on-fail`"
      @update:model-value="onOnFail"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useProfessionalDocumentCatalogStore } from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalSealKey } from '@/types/professional-documents.types'
import {
  QualificationOnFail,
  QualificationRuleField
} from '@/types/qualification-eligibility.types'
import type { QualificationRuleValue } from '@/types/qualification-policy.types'

import OnFailSelector from './OnFailSelector.vue'
import { setCriterionField, setCriterionKey, setCriterionOnFail } from './policyModel'

const props = withDefaults(
  defineProps<{ modelValue: QualificationRuleValue | null; testId?: string }>(),
  { testId: 'qualification-criterion' }
)
const emit = defineEmits<{ 'update:modelValue': [value: QualificationRuleValue] }>()

const change = (value: QualificationRuleValue) => emit('update:modelValue', value)

const catalog = useProfessionalDocumentCatalogStore()

const FIELD_OPTIONS = [
  { title: 'Selo', value: QualificationRuleField.SEAL },
  { title: 'Documento', value: QualificationRuleField.DOCUMENT }
]

// Suporte a Vida é atalho de regra (grupo de ATLS/ACLS/PALS), não selo: o backend recusa
// `{ field: SEAL, key: LIFE_SUPPORT }` (D35, A3.9, ruling R45).
const sealOptions = computed(() =>
  catalog.seals
    .filter((seal) => seal.key !== ProfessionalSealKey.LIFE_SUPPORT)
    .map((seal) => ({ title: seal.name, value: seal.key }))
)
const documentOptions = computed(() =>
  catalog.types.map((type) => ({ title: type.name, value: type.key }))
)

const onField = (field: QualificationRuleField) =>
  change(setCriterionField(props.modelValue, field))
// O autocomplete limpo devolve `null`: o critério volta a ficar incompleto (chave vazia).
const onKey = (key: string | null) => change(setCriterionKey(props.modelValue, key ?? ''))
const onOnFail = (onFail: QualificationOnFail) =>
  change(setCriterionOnFail(props.modelValue, onFail))
</script>
